/**
 * Site-wide interaction tracking (reusable across sites).
 *
 * Captures, in real time, every:
 *   • click       — viewport + page coordinates (normalized for heatmaps), target descriptor
 *   • keypress     — key identity + modifiers (values masked inside sensitive fields)
 *   • mouse move   — throttled sample, normalized coordinates
 *   • page view    — SPA route changes
 *
 * Every event carries a millisecond epoch timestamp (`t`) plus a `dt` offset from
 * session start, so the admin dashboard can show exact "+1,234 ms" timing and a
 * real-time click heatmap.
 *
 * Data is streamed to Firestore at:  siteEvents/{siteId}/sessions/{sessionId}
 *
 * ── Reuse on another site ────────────────────────────────────────────────
 *   import { startSiteTracking, stopSiteTracking } from './utils/siteTracking';
 *   startSiteTracking({ siteId: 'example.com' });   // call once, when site id is known
 *   // ...or just mount <SiteTrackingProvider> (see SiteTrackingContext.jsx).
 * The only per-site requirement is a Firestore `db` export at ../firebase/firebaseConfig.
 */
import db from '../firebase/firebaseConfig';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

const ROOT_COLLECTION = 'siteEvents';
const SESSIONS_SUBCOLLECTION = 'sessions';
const VISITOR_KEY = 'hcva_visitor_id';

/** Keep the events array safely under Firestore's 1MB document limit. */
const MAX_EVENTS = 1200;
/** How often buffered events are flushed to Firestore (kept low for "real time" feel). */
const FLUSH_DEBOUNCE_MS = 600;
/** Hard cap between flushes so a busy session still streams steadily. */
const FLUSH_MAX_WAIT_MS = 2000;
/** Mouse-move sampling rate (one stored sample per interval at most). */
const MOVE_SAMPLE_MS = 120;

const hasWindow = typeof window !== 'undefined';
const hasDoc = typeof document !== 'undefined';

/* ── Identity helpers ──────────────────────────────────────────────────── */

function randomId(len = 14) {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let s = '';
  for (let i = 0; i < len; i += 1) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}

/** Stable anonymous visitor id (shared with redirect tracking via the same key). */
export function getVisitorId() {
  if (!hasWindow) return 'ssr';
  try {
    let id = window.localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = `v_${Date.now().toString(36)}_${randomId(12)}`;
      window.localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch (_) {
    return `v_${randomId(12)}`;
  }
}

function detectDeviceType(ua) {
  const s = (ua || '').toLowerCase();
  if (/ipad|tablet|(android(?!.*mobile))/.test(s)) return 'tablet';
  if (/mobi|iphone|ipod|android.*mobile|windows phone/.test(s)) return 'mobile';
  return 'desktop';
}

function detectBrowser(ua) {
  const u = ua || '';
  if (/Edg\//i.test(u)) return 'Edge';
  if (/OPR\/|Opera/i.test(u)) return 'Opera';
  if (/Firefox\//i.test(u)) return 'Firefox';
  if (/Chrome\/|CriOS/i.test(u)) return 'Chrome';
  if (/Safari/i.test(u) && !/Chrome/i.test(u)) return 'Safari';
  return 'Unknown';
}

function detectOs(ua, platform) {
  const u = (ua || '').toLowerCase();
  if (/iphone|ipad|ipod/.test(u)) return 'iOS';
  if (/android/.test(u)) return 'Android';
  if (/macintosh|mac os x/.test(u)) return 'macOS';
  if (/windows/.test(u)) return 'Windows';
  if (/linux/.test(u)) return 'Linux';
  const p = (platform || '').toLowerCase();
  if (p.includes('mac')) return 'macOS';
  if (p.includes('win')) return 'Windows';
  return 'Unknown';
}

function collectDeviceInfo() {
  if (!hasWindow) return { deviceType: 'ssr' };
  const ua = navigator.userAgent || '';
  let timezone = '';
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (_) {
    timezone = '';
  }
  return {
    userAgent: ua,
    platform: navigator.platform || '',
    language: navigator.language || '',
    deviceType: detectDeviceType(ua),
    browser: detectBrowser(ua),
    os: detectOs(ua, navigator.platform),
    screenW: window.screen?.width || 0,
    screenH: window.screen?.height || 0,
    viewportW: window.innerWidth || 0,
    viewportH: window.innerHeight || 0,
    devicePixelRatio: window.devicePixelRatio || 1,
    timezone,
  };
}

/* ── Privacy: never persist real characters typed into sensitive fields ──── */

const SENSITIVE_PATTERN = /pass|pwd|card|cc-|cvv|cvc|ccv|secur|ssn|social|pin|otp|email|e-mail|tel|phone/i;

function isSensitiveTarget(target) {
  if (!target || typeof target !== 'object') return false;
  const tag = (target.tagName || '').toLowerCase();
  if (tag !== 'input' && tag !== 'textarea') return false;
  const type = (target.getAttribute?.('type') || '').toLowerCase();
  if (type === 'password' || type === 'email' || type === 'tel') return true;
  const attrs = [
    target.getAttribute?.('name') || '',
    target.getAttribute?.('id') || '',
    target.getAttribute?.('autocomplete') || '',
    target.getAttribute?.('aria-label') || '',
  ].join(' ');
  return SENSITIVE_PATTERN.test(attrs);
}

/** Compact, safe description of an element (no PII beyond short visible label). */
function describeTarget(target) {
  if (!target || typeof target !== 'object') return null;
  const tag = (target.tagName || '').toLowerCase();
  if (!tag) return null;
  let text = '';
  try {
    text = (target.innerText || target.textContent || '').trim().slice(0, 60);
  } catch (_) {
    text = '';
  }
  let cls = '';
  try {
    cls = typeof target.className === 'string' ? target.className.slice(0, 100) : '';
  } catch (_) {
    cls = '';
  }
  return {
    tag,
    id: target.id || '',
    cls,
    text,
    href: target.getAttribute ? target.getAttribute('href') || '' : '',
  };
}

/* ── Session state (module singleton — one site session per tab) ─────────── */

let session = null;
let flushTimer = null;
let firstDirtyAt = 0;
let lastMoveAt = 0;
let listenersBound = false;

function clearFlushTimer() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}

function buildPayload(final) {
  if (!session) return null;
  const events = session.events.slice(-MAX_EVENTS);
  return {
    siteId: session.siteId,
    sessionId: session.id,
    visitorId: session.visitorId,
    entryPath: session.entryPath,
    currentPath: hasWindow ? window.location.pathname : session.entryPath,
    referrer: session.referrer,
    device: session.device,
    startedAt: session.startedAtTs,
    startedAtMs: session.startedAtMs,
    lastActivityAt: serverTimestamp(),
    lastActivityMs: Date.now(),
    counts: {
      clicks: session.clickCount,
      keys: session.keyCount,
      moves: session.moveCount,
      pageviews: session.pageviewCount,
      total: session.seq,
    },
    eventsTruncated: session.truncated,
    events,
    closed: Boolean(final),
    ...(final
      ? {
          endedAt: serverTimestamp(),
          endedAtMs: Date.now(),
          endedReason: session.endedReason || 'unknown',
          durationMs: Date.now() - session.startedAtMs,
        }
      : {}),
  };
}

async function writeSession(final) {
  if (!session) return;
  const payload = buildPayload(final);
  if (!payload) return;
  session.dirty = false;
  firstDirtyAt = 0;
  try {
    await setDoc(
      doc(db, ROOT_COLLECTION, session.siteId, SESSIONS_SUBCOLLECTION, session.id),
      payload,
      { merge: true }
    );
  } catch (err) {
    session.dirty = true;
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[siteTracking] flush failed:', err);
    }
  }
}

function scheduleFlush() {
  if (!session) return;
  session.dirty = true;
  if (!firstDirtyAt) firstDirtyAt = Date.now();
  // Force a flush if we've been buffering longer than the hard cap.
  if (Date.now() - firstDirtyAt >= FLUSH_MAX_WAIT_MS) {
    clearFlushTimer();
    writeSession(false);
    return;
  }
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    if (session && session.dirty) writeSession(false);
  }, FLUSH_DEBOUNCE_MS);
}

/** Drop oldest mouse-move events first so clicks/keys (heatmap data) survive trimming. */
function trimEvents() {
  if (!session || session.events.length <= MAX_EVENTS) return;
  session.truncated = true;
  let over = session.events.length - MAX_EVENTS;
  const kept = [];
  let dropped = 0;
  for (let i = 0; i < session.events.length; i += 1) {
    const ev = session.events[i];
    if (dropped < over && i !== 0 && ev.type === 'move') {
      dropped += 1;
      continue;
    }
    kept.push(ev);
  }
  session.events = kept;
  if (session.events.length > MAX_EVENTS) {
    const first = session.events[0];
    session.events = [first, ...session.events.slice(-(MAX_EVENTS - 1))];
  }
}

function pushEvent(type, data) {
  if (!session) return;
  session.seq += 1;
  const now = Date.now();
  session.events.push({
    seq: session.seq,
    t: now,
    dt: now - session.startedAtMs,
    type,
    ...data,
  });
  if (session.events.length > MAX_EVENTS + 60) trimEvents();
  scheduleFlush();
}

/* ── DOM event handlers ────────────────────────────────────────────────── */

function onClick(e) {
  if (!session || !e) return;
  const vw = hasWindow ? window.innerWidth || 1 : 1;
  const vh = hasWindow ? window.innerHeight || 1 : 1;
  const x = Math.round(e.clientX || 0);
  const y = Math.round(e.clientY || 0);
  session.clickCount += 1;
  pushEvent('click', {
    x,
    y,
    pageX: Math.round(e.pageX || 0),
    pageY: Math.round(e.pageY || 0),
    nx: Math.min(1, Math.max(0, x / vw)),
    ny: Math.min(1, Math.max(0, y / vh)),
    vw,
    vh,
    button: typeof e.button === 'number' ? e.button : 0,
    path: hasWindow ? window.location.pathname : '',
    target: describeTarget(e.target),
  });
}

function onKeyDown(e) {
  if (!session || !e) return;
  const sensitive = isSensitiveTarget(e.target);
  const printable = typeof e.key === 'string' && e.key.length === 1;
  session.keyCount += 1;
  pushEvent('key', {
    key: sensitive && printable ? '•' : e.key || '',
    code: e.code || '',
    printable,
    masked: Boolean(sensitive && printable),
    alt: Boolean(e.altKey),
    ctrl: Boolean(e.ctrlKey),
    shift: Boolean(e.shiftKey),
    meta: Boolean(e.metaKey),
    repeat: Boolean(e.repeat),
    path: hasWindow ? window.location.pathname : '',
    targetTag: (e.target?.tagName || '').toLowerCase(),
  });
}

function onMouseMove(e) {
  if (!session || !e) return;
  const now = Date.now();
  if (now - lastMoveAt < MOVE_SAMPLE_MS) return;
  lastMoveAt = now;
  const vw = hasWindow ? window.innerWidth || 1 : 1;
  const vh = hasWindow ? window.innerHeight || 1 : 1;
  const x = Math.round(e.clientX || 0);
  const y = Math.round(e.clientY || 0);
  session.moveCount += 1;
  pushEvent('move', {
    x,
    y,
    nx: Math.min(1, Math.max(0, x / vw)),
    ny: Math.min(1, Math.max(0, y / vh)),
    vw,
    vh,
  });
}

function onPageHide() {
  if (!session) return;
  pushEvent('page_hide', {
    visibilityState: hasDoc ? document.visibilityState || '' : '',
  });
  if (session.dirty) writeSession(false);
}

function onVisibilityChange() {
  if (hasDoc && document.visibilityState === 'hidden') onPageHide();
}

function bindListeners() {
  if (listenersBound || !hasDoc) return;
  document.addEventListener('click', onClick, true);
  document.addEventListener('keydown', onKeyDown, true);
  document.addEventListener('mousemove', onMouseMove, true);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('beforeunload', onPageHide);
  document.addEventListener('visibilitychange', onVisibilityChange);
  listenersBound = true;
}

function unbindListeners() {
  if (!listenersBound || !hasDoc) return;
  document.removeEventListener('click', onClick, true);
  document.removeEventListener('keydown', onKeyDown, true);
  document.removeEventListener('mousemove', onMouseMove, true);
  window.removeEventListener('pagehide', onPageHide);
  window.removeEventListener('beforeunload', onPageHide);
  document.removeEventListener('visibilitychange', onVisibilityChange);
  listenersBound = false;
}

/* ── Public API ─────────────────────────────────────────────────────────── */

/**
 * Begin a site-wide tracking session. Idempotent per (siteId): calling again with
 * the same siteId is a no-op so React StrictMode double-mounts won't duplicate.
 */
export function startSiteTracking({ siteId } = {}) {
  if (!hasWindow || !siteId) return null;
  if (session && session.siteId === siteId) return session.id;
  if (session) stopSiteTracking('superseded');

  const now = Date.now();
  session = {
    id: `e_${now.toString(36)}_${randomId(10)}`,
    siteId,
    visitorId: getVisitorId(),
    entryPath: hasWindow ? window.location.pathname : '',
    referrer: hasDoc ? document.referrer || '' : '',
    device: collectDeviceInfo(),
    startedAtTs: serverTimestamp(),
    startedAtMs: now,
    events: [],
    seq: 0,
    clickCount: 0,
    keyCount: 0,
    moveCount: 0,
    pageviewCount: 0,
    truncated: false,
    dirty: false,
    endedReason: null,
  };

  bindListeners();
  pushEvent('session_start', { path: session.entryPath });
  recordPageView(session.entryPath);
  writeSession(false);
  return session.id;
}

/** Record an SPA route change. Safe to call on every navigation. */
export function recordPageView(path) {
  if (!session) return;
  session.pageviewCount += 1;
  pushEvent('pageview', {
    path: path || (hasWindow ? window.location.pathname : ''),
    title: hasDoc ? document.title || '' : '',
  });
}

/** Stop the active session and flush a final document immediately. */
export function stopSiteTracking(reason = 'stopped') {
  if (!session) return;
  session.endedReason = reason;
  pushEvent('session_end', { reason, durationMs: Date.now() - session.startedAtMs });
  clearFlushTimer();
  const finalWrite = writeSession(true);
  unbindListeners();
  session = null;
  return finalWrite;
}

export function isSiteTracking() {
  return Boolean(session);
}

/** Live, in-memory snapshot of the current session counts (for debug UIs). */
export function getSiteTrackingStats() {
  if (!session) return null;
  return {
    sessionId: session.id,
    siteId: session.siteId,
    clicks: session.clickCount,
    keys: session.keyCount,
    moves: session.moveCount,
    pageviews: session.pageviewCount,
    total: session.seq,
    startedAtMs: session.startedAtMs,
  };
}
